import { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell, Box, Divider, Group, Text, Center, Loader, ScrollArea, NavLink, Badge, Button, UnstyledButton } from '@mantine/core';
import { Building2, Users, CreditCard, Receipt, Settings, LogOut, ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { Roles } from '@bmai/shared';
import { UltraTraceLogo, UltraTraceWordmark } from '@/design/UltraTraceLogo';
import { useAuth } from '@/features/auth/AuthContext';

const ADMIN_ONLY = [Roles.PLATFORM_OPERATOR];

const NAV: OperatorNavItem[] = [
    { path: '/operator/tenants', label: 'Tenants', icon: Building2, roles: ADMIN_ONLY },
    { path: '/operator/plans', label: 'Plans', icon: CreditCard },
    { path: '/operator/billing', label: 'Billing', icon: Receipt },
    { path: '/operator/users', label: 'Operators', icon: Users, roles: ADMIN_ONLY },
    { path: '/operator/audit', label: 'Audit', icon: ScrollText, roles: ADMIN_ONLY },
];

const SETTINGS_NAV: OperatorNavItem = { path: '/operator/settings', label: 'Settings', icon: Settings };

const collapsedIconStyles = {
    root: { display: 'flex', justifyContent: 'center', padding: '8px 0' },
    body: { flex: 'unset' },
    section: { marginRight: 0, marginInlineEnd: 0 },
} as const;

interface OperatorNavItem {
    path: string;
    label: string;
    icon: typeof Building2;
    /** Realm-role allow-list; the item shows if the user holds any listed role (omit = everyone). */
    roles?: string[];
}

function renderNavItem(
    item: OperatorNavItem,
    pathname: string,
    collapsed: boolean,
    navigate: (to: string) => void,
): React.ReactElement {
    const Icon = item.icon;
    const active = pathname === item.path || pathname.startsWith(`${item.path}/`);
    return (
        <NavLink
            key={item.path}
            label={collapsed ? undefined : item.label}
            leftSection={<Icon size={18} strokeWidth={active ? 2.5 : 2} />}
            active={active}
            title={collapsed ? item.label : undefined}
            variant="light"
            mb={2}
            onClick={() => navigate(item.path)}
            styles={collapsed ? collapsedIconStyles : { label: { fontWeight: active ? 700 : 500 } }}
        />
    );
}

/**
 * Self-contained shell for the vendor operator console (SAAS-32) — separate from
 * the tenant AppLayout but mirroring its chrome: a top header with the UltraTrace
 * wordmark, a collapsible left sidebar (same tokens, MantineNavLink light variant,
 * chevron toggle). Gated to either the platform-operator (admin) or
 * operator-billing role; anyone else is bounced to the operator sign-in. Billing
 * operators see only the billing-facing surfaces (Plans, Billing, Settings).
 */
export default function OperatorLayout(): React.ReactElement {
    const { isLoading, isAuthenticated, hasRole, user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);

    if (isLoading) {
        return <Center h="100vh"><Loader /></Center>;
    }
    const isAdmin = hasRole(Roles.PLATFORM_OPERATOR);
    const isOperator = isAdmin || hasRole(Roles.OPERATOR_BILLING);
    if (!isAuthenticated || !isOperator) {
        return <Navigate to="/operator/login" replace />;
    }

    const canSee = (item: OperatorNavItem): boolean => !item.roles || item.roles.some((role) => hasRole(role));
    const visibleNav = NAV.filter(canSee);
    const railWidth = collapsed ? 64 : 240;

    return (
        <AppShell
            header={{ height: 56 }}
            navbar={{ width: railWidth, breakpoint: 'sm', collapsed: { mobile: false, desktop: false } }}
            padding={0}
            styles={{
                header: { backgroundColor: 'var(--bm-color-body)', borderBottom: '1px solid var(--bm-color-border)' },
                navbar: { backgroundColor: 'var(--bm-color-body)', borderRight: '1px solid var(--bm-color-border)', transition: 'width 200ms ease', overflow: 'visible' },
                main: { backgroundColor: 'var(--bm-color-canvas)', height: '100vh', overflow: 'hidden' },
            }}
        >
            <AppShell.Header>
                <Group h="100%" gap={0} align="center" wrap="nowrap">
                    {/* Logo block — navbar width, aligned above the sidebar */}
                    <Group
                        w={railWidth}
                        h="100%"
                        px={collapsed ? 0 : 18}
                        align="center"
                        justify={collapsed ? 'center' : 'flex-start'}
                        wrap="nowrap"
                        style={{ flexShrink: 0, borderRight: '1px solid var(--bm-color-border)', transition: 'width 200ms ease' }}
                    >
                        {collapsed ? <UltraTraceLogo size={24} title="UltraTrace" /> : <UltraTraceWordmark size={22} title="UltraTrace" />}
                    </Group>
                    {/* Content-area header — starts to the right of the sidebar */}
                    <Group h="100%" px="md" gap="sm" align="center" wrap="nowrap" justify="space-between" style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="sm" align="center" wrap="nowrap">
                            <Text fw={700} size="sm">Operator Console</Text>
                            <Badge color={isAdmin ? 'grape' : 'blue'} variant="light" size="xs">{isAdmin ? 'platform' : 'billing'}</Badge>
                        </Group>
                        <Group gap="sm" align="center" wrap="nowrap" justify="flex-end">
                            {user?.email && <Text size="sm" c="dimmed" visibleFrom="sm">{user.email}</Text>}
                            <Button size="xs" variant="subtle" leftSection={<LogOut size={15} />} onClick={logout}>Sign out</Button>
                        </Group>
                    </Group>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar>
                <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                    <UnstyledButton
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                        title={collapsed ? 'Expand' : 'Collapse'}
                        style={{
                            position: 'absolute', top: '50%', right: 0, transform: 'translate(50%, -50%)',
                            width: 24, height: 24, borderRadius: '50%',
                            background: 'var(--bm-color-body)', border: '1px solid var(--bm-color-border)',
                            boxShadow: 'var(--bm-card-shadow)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--bm-color-text-dimmed)', zIndex: 10,
                        }}
                    >
                        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </UnstyledButton>

                    <Box px={collapsed ? 4 : 12} pt="md">
                        {visibleNav.map((item) => renderNavItem(item, location.pathname, collapsed, navigate))}
                    </Box>

                    {canSee(SETTINGS_NAV) && (
                        <Box px={collapsed ? 4 : 12} pb="md" style={{ marginTop: 'auto' }}>
                            <Divider mb="xs" color="var(--bm-color-border)" />
                            {renderNavItem(SETTINGS_NAV, location.pathname, collapsed, navigate)}
                        </Box>
                    )}
                </Box>
            </AppShell.Navbar>

            <AppShell.Main>
                {location.pathname.startsWith('/operator/settings') ? (
                    // Settings owns its own full-bleed frame (rail + scroll), flush to the
                    // navbar — matching the tenant app's Settings layout.
                    <Outlet />
                ) : (
                    <ScrollArea h="calc(100vh - 56px)" type="hover" scrollbarSize={8}>
                        <Box p={{ base: 'sm', sm: 'md' }}>
                            <Box maw={1400} mx="auto">
                                <Outlet />
                            </Box>
                        </Box>
                    </ScrollArea>
                )}
            </AppShell.Main>
        </AppShell>
    );
}
